import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Package, FileText, Plus, ExternalLink } from "lucide-react";
import { safeFormat } from "@/lib/dateUtils";
import { useSortableTable } from "@/hooks/useSortableTable";

export default function ProductDetail({ product, open, onClose, onUpdated }) {
  const [purchases, setPurchases] = useState([]);
  const [usage, setUsage] = useState([]);
  const [addPurchase, setAddPurchase] = useState(false);
  const [purchaseForm, setPurchaseForm] = useState({ supplier: '', invoice_number: '', qty: 1, unit_cost: 0, notes: '' });

  useEffect(() => {
    if (open && product?.id) {
      axios.get(`${API}/inventory/products/${product.id}/purchases`).then(r => setPurchases(r.data.purchases || [])).catch(() => {});
      axios.get(`${API}/inventory/movements?product_id=${product.id}&limit=50`).then(r => {
        setUsage((r.data.movements || []).filter(m => m.type === 'Usage'));
      }).catch(() => {});
    }
  }, [open, product?.id]);

  const { sortedData: sortedPurchases, SortHeader } = useSortableTable(purchases, 'created_at', 'desc');

  const savePurchase = async () => {
    try {
      await axios.post(`${API}/inventory/products/${product.id}/restock`, {
        bottles: parseInt(purchaseForm.qty) || 0, uses: 0
      });
      // Also log with supplier info
      await axios.post(`${API}/inventory/movements`, {
        product_id: product.id, type: 'Purchase',
        qty: parseInt(purchaseForm.qty) || 0,
        unit_cost: parseFloat(purchaseForm.unit_cost) || 0,
        notes: `${purchaseForm.supplier}${purchaseForm.invoice_number ? ' #' + purchaseForm.invoice_number : ''} - ${purchaseForm.notes}`
      });
      toast.success('Purchase recorded');
      setAddPurchase(false);
      setPurchaseForm({ supplier: '', invoice_number: '', qty: 1, unit_cost: 0, notes: '' });
      // Reload
      const r = await axios.get(`${API}/inventory/products/${product.id}/purchases`);
      setPurchases(r.data.purchases || []);
      if (onUpdated) onUpdated();
    } catch (e) { toast.error('Failed'); }
  };

  if (!product) return null;

  const costPerUse = product.max_uses_per_bottle > 0 ? (product.unit_cost || 0) / product.max_uses_per_bottle : 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-2xl max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Playfair Display' }} className="flex items-center gap-2">
            <Package size={20} className="text-primary" />
            {product.name}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="details">
          <TabsList className="rounded-xl mb-4">
            <TabsTrigger value="details" className="rounded-lg text-xs">Details</TabsTrigger>
            <TabsTrigger value="stock" className="rounded-lg text-xs">Stock</TabsTrigger>
            <TabsTrigger value="purchases" className="rounded-lg text-xs flex items-center gap-1">
              Purchases
              {purchases.length > 0 && <Badge className="ml-1 text-[9px] h-4 px-1 bg-primary text-white rounded-full">{purchases.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="usage" className="rounded-lg text-xs">Usage</TabsTrigger>
          </TabsList>

          {/* Details tab */}
          <TabsContent value="details">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="p-3 bg-muted/30 rounded-xl"><p className="text-[10px] text-muted-foreground">Brand</p><p className="font-medium">{product.brand || '-'}</p></div>
              <div className="p-3 bg-muted/30 rounded-xl"><p className="text-[10px] text-muted-foreground">SKU</p><p className="font-medium">{product.sku || '-'}</p></div>
              <div className="p-3 bg-muted/30 rounded-xl"><p className="text-[10px] text-muted-foreground">Category</p><p className="font-medium">{product.category || '-'}</p></div>
              <div className="p-3 bg-muted/30 rounded-xl"><p className="text-[10px] text-muted-foreground">Unit Cost</p><p className="font-medium text-primary">£{(product.unit_cost || 0).toFixed(2)}</p></div>
              <div className="p-3 bg-muted/30 rounded-xl"><p className="text-[10px] text-muted-foreground">Max Uses / Bottle</p><p className="font-medium">{product.max_uses_per_bottle || 70}</p></div>
              <div className="p-3 bg-muted/30 rounded-xl"><p className="text-[10px] text-muted-foreground">Cost per Use</p><p className="font-medium">£{costPerUse.toFixed(3)}</p></div>
            </div>
          </TabsContent>

          {/* Stock tab */}
          <TabsContent value="stock">
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="p-4 bg-muted/30 rounded-xl text-center">
                <p className="text-2xl font-semibold tabular-nums" style={{ fontFamily: 'Playfair Display' }}>{product.uses_on_hand || 0}</p>
                <p className="text-[10px] text-muted-foreground">Uses On Hand</p>
              </div>
              <div className="p-4 bg-muted/30 rounded-xl text-center">
                <p className="text-2xl font-semibold tabular-nums" style={{ fontFamily: 'Playfair Display' }}>{product.stock_on_hand || 0}</p>
                <p className="text-[10px] text-muted-foreground">Bottles</p>
              </div>
              <div className="p-4 bg-muted/30 rounded-xl text-center">
                <p className="text-2xl font-semibold tabular-nums" style={{ fontFamily: 'Playfair Display' }}>{product.reorder_level_uses || 10}</p>
                <p className="text-[10px] text-muted-foreground">Reorder At</p>
              </div>
            </div>
            {(product.uses_on_hand || 0) <= (product.reorder_level_uses || 10) && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 font-medium">
                Low stock - reorder recommended
              </div>
            )}
          </TabsContent>

          {/* Purchase History tab */}
          <TabsContent value="purchases">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">{purchases.length} purchase records</p>
                <Button size="sm" onClick={() => setAddPurchase(!addPurchase)} className="rounded-xl h-7 text-xs">
                  <Plus size={12} className="mr-1" /> Add Purchase
                </Button>
              </div>

              {/* Add purchase form */}
              {addPurchase && (
                <div className="p-3 bg-muted/30 rounded-xl space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[10px]">Supplier</Label><Input value={purchaseForm.supplier} onChange={e => setPurchaseForm(p => ({...p, supplier: e.target.value}))} className="mt-1 rounded-lg h-7 text-xs" /></div>
                    <div><Label className="text-[10px]">Invoice #</Label><Input value={purchaseForm.invoice_number} onChange={e => setPurchaseForm(p => ({...p, invoice_number: e.target.value}))} className="mt-1 rounded-lg h-7 text-xs" /></div>
                    <div><Label className="text-[10px]">Qty (bottles)</Label><Input type="number" value={purchaseForm.qty} onChange={e => setPurchaseForm(p => ({...p, qty: e.target.value}))} className="mt-1 rounded-lg h-7 text-xs" /></div>
                    <div><Label className="text-[10px]">Unit Cost (£)</Label><Input type="number" step="0.01" value={purchaseForm.unit_cost} onChange={e => setPurchaseForm(p => ({...p, unit_cost: e.target.value}))} className="mt-1 rounded-lg h-7 text-xs" /></div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={savePurchase} className="rounded-lg h-7 text-xs">Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setAddPurchase(false)} className="rounded-lg h-7 text-xs">Cancel</Button>
                  </div>
                </div>
              )}

              {/* Purchase history table */}
              {sortedPurchases.length > 0 ? (
                <div className="border rounded-xl overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="text-left p-2 font-medium text-muted-foreground">Date</th>
                        <th className="text-left p-2 font-medium text-muted-foreground">Supplier</th>
                        <th className="text-left p-2 font-medium text-muted-foreground">Invoice #</th>
                        <th className="text-left p-2 font-medium text-muted-foreground">Qty</th>
                        <th className="text-left p-2 font-medium text-muted-foreground">Unit Cost</th>
                        <th className="text-left p-2 font-medium text-muted-foreground">Total</th>
                        <th className="text-left p-2 font-medium text-muted-foreground w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPurchases.map(p => (
                        <tr key={p.id} className="border-b border-border/50 hover:bg-muted/10">
                          <td className="p-2">{safeFormat(p.date || p.created_at, 'd MMM yyyy')}</td>
                          <td className="p-2">{p.supplier_name || (p.notes || '').split(' #')[0] || '-'}</td>
                          <td className="p-2">{p.invoice_number || (p.notes || '').match(/#(\w+)/)?.[1] || '-'}</td>
                          <td className="p-2 tabular-nums">{p.qty || 0}{p.uses_added ? ` (${p.uses_added} uses)` : ''}</td>
                          <td className="p-2 tabular-nums">£{(p.unit_cost || 0).toFixed(2)}</td>
                          <td className="p-2 tabular-nums font-medium">£{((p.unit_cost || 0) * (p.qty || 0)).toFixed(2)}</td>
                          <td className="p-2">
                            {p.invoice_id && (
                              <a href={`${API}/invoices/file/${p.invoice_id}`} target="_blank" rel="noreferrer">
                                <Button variant="ghost" size="icon" className="h-5 w-5 rounded" title="View Invoice">
                                  <ExternalLink size={10} />
                                </Button>
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-8 text-center text-xs text-muted-foreground">No purchase history yet</div>
              )}
            </div>
          </TabsContent>

          {/* Usage History tab */}
          <TabsContent value="usage">
            {usage.length > 0 ? (
              <div className="border rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left p-2 font-medium text-muted-foreground">Date</th>
                      <th className="text-left p-2 font-medium text-muted-foreground">Uses</th>
                      <th className="text-left p-2 font-medium text-muted-foreground">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usage.map(u => (
                      <tr key={u.id} className="border-b border-border/50">
                        <td className="p-2">{safeFormat(u.date || u.created_at, 'd MMM yyyy')}</td>
                        <td className="p-2 tabular-nums">{u.uses_deducted || u.qty || 0}</td>
                        <td className="p-2 text-muted-foreground">{u.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-8 text-center text-xs text-muted-foreground">No usage history yet</div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
